import React, { useEffect } from "react";
import { Modal, Form, Input, InputNumber, Button, notification } from "antd";
import { useRecurringMemberItemsService } from "../../../services/recurring/RecurringMemberItemsServiceProvider";
import { RecurringMemberItemCatalog } from "../../../model/recurring/RecurringMemberItems";
import { useCurrencySymbol } from "../../../hooks/useFormatters";

interface CreateRecurringMemberItemModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    clientName: string;
}

interface FormValues {
    itemName: string;
    price: number;
}

const CreateRecurringMemberItemModal: React.FC<CreateRecurringMemberItemModalProps> = ({
    visible,
    onClose,
    onSuccess,
    clientName,
}) => {
    const [form] = Form.useForm<FormValues>();
    const recurringService = useRecurringMemberItemsService();
    const [saving, setSaving] = React.useState(false);
    const currencySymbol = useCurrencySymbol();

    useEffect(() => {
        if (visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const request: RecurringMemberItemCatalog = {
                id: 0,
                clientName,
                itemName: values.itemName,
                price: values.price,
            };
            setSaving(true);
            await recurringService.createCatalogItem(request);
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Error creating recurring item:", error);
            if (error instanceof Error && !error.message.includes("validation")) {
                notification.error({
                    message: "Error",
                    description: "Failed to create recurring item",
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title="Create new recurring item"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={400}
            destroyOnClose
        >
            <Form<FormValues>
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label="Item name"
                    name="itemName"
                    rules={[{ required: true, message: "Please enter item name" }]}
                >
                    <Input placeholder="Enter item name" />
                </Form.Item>
                <Form.Item
                    label={`Price (${currencySymbol})`}
                    name="price"
                    rules={[
                        { required: true, message: "Please enter a price" },
                        { type: "number", min: 0, message: "Price must be positive" },
                    ]}
                >
                    <InputNumber
                        inputMode="decimal"
                        style={{ width: "100%" }}
                        placeholder="0.00"
                        precision={2}
                        min={0}
                        step={0.01}
                        prefix={currencySymbol}
                    />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
                    <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        Create
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateRecurringMemberItemModal;
